/**
 * Extract the base resource from a URL for targeted query invalidation.
 * e.g. "dashboard/invoices/sales/123/edit" → "dashboard/invoices"
 */
export const getQueryResourceKey = (url) => {
  if (!url) return undefined;
  const base = url.split("?")[0];
  const parts = base.split("/").filter(Boolean);
  return parts.slice(0, 2).join("/");
};

/**
 * Invalidate React Query cache entries whose first queryKey segment
 * starts with the resource derived from `url`.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {string} url - API path, e.g. "dashboard/dues" or "dashboard/contracts/1/terminate"
 */
export const invalidateQueries = (queryClient, url) => {
  const resource = getQueryResourceKey(url);
  if (!resource) return;

  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      return typeof key === "string" && key.startsWith(resource);
    },
  });
};

/**
 * Invalidate the paged option lists `LoadMoreSelect` holds for one search endpoint. They come
 * from `useInfiniteApiQuery`, keyed `["infinite", url, …]`, so the resource-prefix match in
 * `invalidateQueries` never reaches them — a picker would go on offering what a write just used up.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {string} url - the search endpoint exactly as the picker names it, e.g. "dashboard/inventories-stocks-search"
 */
export const invalidateInfiniteSearch = (queryClient, url) =>
  queryClient.invalidateQueries({ queryKey: ["infinite", url] });

/**
 * Invalidate rent-contract lists embedded in a parent entity's profile
 * (property/unit "Contracts" tab, project "Rent Contracts" tab). These
 * queries key off the parent entity's URL (e.g. "dashboard/properties/contracts/{id}"),
 * not "dashboard/contracts", so mutations scoped to a single contract
 * (publish, delete, service-assign, unlink) never reach them via the
 * default resource-prefix invalidation and must invalidate explicitly.
 */
export const invalidateEmbeddedRentContractLists = (queryClient) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      if (typeof key !== "string") return false;
      return (
        key.includes("/units/contracts/") ||
        key.includes("/properties/contracts/")
      );
    },
  });
};
